import { ReactNode } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import type { GrowerInfo, GrowerSortOption, JobCardInfo } from "./types";
import type { MobileAssignmentTab } from "./useMobileWizard";
import { MobileUnassignedList } from "./MobileUnassignedList";
import { MobileEntityList } from "./MobileEntityList";

interface MobileAssignmentLayoutProps<TEntity extends { id: string }> {
	activeTab: MobileAssignmentTab;
	onTabChange: (tab: MobileAssignmentTab) => void;
	instructionText: string;
	// Unassigned panel props
	unassignedGrowers: GrowerInfo[];
	unassignedJobs: JobCardInfo[];
	growerSortOption: GrowerSortOption;
	setGrowerSortOption: (opt: GrowerSortOption) => void;
	selectedItems: Set<string>;
	clearSelection: () => void;
	handleItemClick: (draggableId: string, shiftKey: boolean) => void;
	onSelectAll?: () => void;
	onExplodeGrower?: (growerName: string) => void;
	onRegroupGrower?: (growerName: string) => void;
	// Entity panel props
	entities: TEntity[];
	growersByEntity: Map<string, GrowerInfo[]>;
	jobsByEntity: Map<string, JobCardInfo[]>;
	assignSelectedToEntity: (entityId: string) => void;
	assignAllToEntity: (entityId: string) => void;
	assignUnassignedToEntity: (entityId: string) => void;
	unassignAllFromEntity: (entityId: string) => void;
	renderEntityHeader: (entity: TEntity) => ReactNode;
	entityHeaderBar?: ReactNode;
	emptyEntityMessage: string;
	entityFooter?: ReactNode;
}

export function MobileAssignmentLayout<TEntity extends { id: string }>({
	activeTab,
	onTabChange,
	instructionText,
	unassignedGrowers,
	unassignedJobs,
	growerSortOption,
	setGrowerSortOption,
	selectedItems,
	clearSelection,
	handleItemClick,
	onSelectAll,
	onExplodeGrower,
	onRegroupGrower,
	entities,
	growersByEntity,
	jobsByEntity,
	assignSelectedToEntity,
	assignAllToEntity,
	assignUnassignedToEntity,
	unassignAllFromEntity,
	renderEntityHeader,
	entityHeaderBar,
	emptyEntityMessage,
	entityFooter,
}: MobileAssignmentLayoutProps<TEntity>) {
	const totalUnassigned = unassignedGrowers.length + unassignedJobs.length;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				{instructionText}
			</Typography>

			<Tabs
				value={activeTab}
				onChange={(_, v) => onTabChange(v)}
				sx={{ mb: 1, minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5, textTransform: "none" } }}
			>
				<Tab label={`Unassigned (${totalUnassigned})`} value="unassigned" />
				<Tab
					label={selectedItems.size > 0 ? `Assign To (${selectedItems.size} selected)` : "Assign To"}
					value="assign-to"
				/>
			</Tabs>

			{activeTab === "unassigned" && (
				<MobileUnassignedList
					unassignedGrowers={unassignedGrowers}
					unassignedJobs={unassignedJobs}
					growerSortOption={growerSortOption}
					setGrowerSortOption={setGrowerSortOption}
					selectedItems={selectedItems}
					clearSelection={clearSelection}
					handleItemClick={handleItemClick}
					onSelectAll={onSelectAll}
					onExplodeGrower={onExplodeGrower}
					onRegroupGrower={onRegroupGrower}
				/>
			)}

			{activeTab === "assign-to" && (
				<MobileEntityList
					entities={entities}
					growersByEntity={growersByEntity}
					jobsByEntity={jobsByEntity}
					selectedItems={selectedItems}
					assignSelectedToEntity={assignSelectedToEntity}
					assignAllToEntity={assignAllToEntity}
					assignUnassignedToEntity={assignUnassignedToEntity}
					unassignAllFromEntity={unassignAllFromEntity}
					renderEntityHeader={renderEntityHeader}
					headerBar={entityHeaderBar}
					emptyMessage={emptyEntityMessage}
					footer={entityFooter}
					onAfterAssign={() => onTabChange("unassigned")}
				/>
			)}
		</Box>
	);
}
